"use client";

import {
    Box,
    Button,
    Paper,
    TextField,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useFormValidation } from "@/hooks/useFormValidation";
import { useToast } from "@/hooks/useToast";
import Toast from "@/components/Toast";

interface IndicationOfInterestFormProps {
    tenant: "nada" | "cada" | "atd";
    studyYear: number;
}

export default function IndicationOfInterestForm({
    tenant,
    studyYear,
}: IndicationOfInterestFormProps) {
    const { toast, showToast, hideToast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [dealershipCount, setDealershipCount] = useState<"single" | "small" | "large">("single");

    const { formState, setValue, setTouched, validateAll } = useFormValidation({
        fullName: "",
        jobTitle: "",
        companyName: "",
        email: "",
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate all fields using the proper validateAll signature
        const isValid = validateAll({
            fullName: { type: "required", label: "Full Name" },
            jobTitle: { type: "required", label: "Job Title" },
            companyName: { type: "required", label: "Dealership / Group Name" },
            email: { type: "email", label: "Email Address" },
        });

        if (!isValid) {
            showToast("Failed to submit. Please try again.", "error");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(
                `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/enrollment/indication-of-interest`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        tenant,
                        study_year: studyYear,
                        full_name: formState.fullName.value,
                        job_title: formState.jobTitle.value,
                        company_name: formState.companyName.value,
                        email: formState.email.value,
                        dealership_count: dealershipCount,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to submit IOI");
            }

            // Success - show thank you message
            setIsSuccess(true);
            showToast("We will notify you when enrollment opens.", "success");
        } catch (error) {
            console.error("IOI submission error:", error);
            showToast("Failed to submit. Please try again.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Success state - show thank you message
    if (isSuccess) {
        return (
            <>
                <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        Thank you for your interest in this year&apos;s study.
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        We will notify you when enrollment opens.
                    </Typography>
                </Paper>
                <Toast toast={toast} onClose={hideToast} />
            </>
        );
    }

    // Form state
    return (
        <>
            <Paper elevation={2} sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                    Interested in Enrolling?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter your contact information here to receive more information on enrolling in
                    the study.
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        {/* Full Name */}
                        <TextField
                            label="Full Name"
                            value={formState.fullName.value}
                            onChange={(e) => setValue("fullName", e.target.value)}
                            onBlur={() => setTouched("fullName")}
                            error={formState.fullName.touched && !!formState.fullName.error}
                            helperText={
                                formState.fullName.touched && formState.fullName.error
                                    ? formState.fullName.error
                                    : ""
                            }
                            required
                            fullWidth
                        />

                        {/* Job Title */}
                        <TextField
                            label="Job Title"
                            value={formState.jobTitle.value}
                            onChange={(e) => setValue("jobTitle", e.target.value)}
                            onBlur={() => setTouched("jobTitle")}
                            error={formState.jobTitle.touched && !!formState.jobTitle.error}
                            helperText={
                                formState.jobTitle.touched && formState.jobTitle.error
                                    ? formState.jobTitle.error
                                    : ""
                            }
                            required
                            fullWidth
                        />

                        {/* Company Name */}
                        <TextField
                            label="Dealership / Group Name"
                            value={formState.companyName.value}
                            onChange={(e) => setValue("companyName", e.target.value)}
                            onBlur={() => setTouched("companyName")}
                            error={formState.companyName.touched && !!formState.companyName.error}
                            helperText={
                                formState.companyName.touched && formState.companyName.error
                                    ? formState.companyName.error
                                    : ""
                            }
                            required
                            fullWidth
                        />

                        {/* Email */}
                        <TextField
                            label="Email Address"
                            type="email"
                            value={formState.email.value}
                            onChange={(e) => setValue("email", e.target.value)}
                            onBlur={() => setTouched("email")}
                            error={formState.email.touched && !!formState.email.error}
                            helperText={
                                formState.email.touched && formState.email.error
                                    ? formState.email.error
                                    : ""
                            }
                            required
                            fullWidth
                        />

                        {/* Dealership Count */}
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ mb: 1 }}>
                                How many dealerships do you want to enroll in the Study?
                            </FormLabel>
                            <RadioGroup
                                value={dealershipCount}
                                onChange={(e) =>
                                    setDealershipCount(
                                        e.target.value as "single" | "small" | "large"
                                    )
                                }
                                row
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio />}
                                    label="Single Dealership"
                                />
                                <FormControlLabel
                                    value="small"
                                    control={<Radio />}
                                    label="2-9 Dealerships"
                                />
                                <FormControlLabel
                                    value="large"
                                    control={<Radio />}
                                    label="10 or More Dealerships"
                                />
                            </RadioGroup>
                        </FormControl>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={isSubmitting}
                            sx={{ mt: 1 }}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    Submitting...
                                </>
                            ) : (
                                "Submit"
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
            <Toast toast={toast} onClose={hideToast} />
        </>
    );
}
